// PLAN-cockpit-v2-iter1 Phase 7 (2026-05-11):
// Collaborate Lens を Shell 統一構成に変更:
// - CollaborateSidebar: Suggestion queue + Status filter + Run detection ボタン
// - CollaborateLens (Main): 選択 suggestion の Detail + Actions
// sessionStorage で selected_suggestion_id を共有、event で双方向同期
// Approve form の article_id は recent articles の autocomplete (datalist)

import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from '../../api/client'
import { useDetailFetch } from '../hooks/useDetailFetch'
import { useToast } from '../components/common/Toast'
import { EmptyState } from '../components/common/EmptyState'
import { useViewport } from '../hooks/useViewport'
import { FilterBar } from '../components/FilterBar'
import type { SearchResponse } from '../../types'

interface Suggestion {
  id: string
  cluster_key: string
  entry_ids: string[]
  proposed_title: string | null
  proposed_one_line: string | null
  proposed_theme: string | null
  proposed_applicability: string | null
  proposed_confidence: string | null
  draft_method: string | null
  status: string
  created_at: string
  cooldown_until?: string | null
}

interface SuggestionsResp {
  total?: number
  suggestions?: Suggestion[]
}

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'cooled'

const SELECTED_KEY = 'cockpit_v2.collab_selected_id'
const FILTER_KEY = 'cockpit_v2.collab_status_filter'
const RECENT_ARTICLES_KEY = 'cockpit_v2.recent_articles'

// ===== 共有 hooks (Sub-sidebar / Main 間で state を sessionStorage + event 経由で共有) =====

function readSelected(): string | null {
  try { return window.sessionStorage.getItem(SELECTED_KEY) } catch { return null }
}
function writeSelected(id: string | null) {
  try {
    if (id) window.sessionStorage.setItem(SELECTED_KEY, id)
    else window.sessionStorage.removeItem(SELECTED_KEY)
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('collab_selected_change'))
}

function readFilter(): StatusFilter {
  try { return (window.sessionStorage.getItem(FILTER_KEY) as StatusFilter) ?? 'pending' } catch { return 'pending' }
}
function writeFilter(v: StatusFilter) {
  try { window.sessionStorage.setItem(FILTER_KEY, v) } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('collab_filter_change'))
}

function dispatchReload() {
  window.dispatchEvent(new CustomEvent('collab_reload'))
}

// ===== Suggestion queue UI (Sub-sidebar 用、モバイル FilterBar でも再利用) =====

interface QueueProps {
  filter: StatusFilter
  selectedId: string | null
  list: Suggestion[]
  loading: boolean
  onFilterChange: (s: StatusFilter) => void
  onSelect: (id: string) => void
  onRunDetection: () => void
}

function SuggestionQueue({ filter, selectedId, list, loading, onFilterChange, onSelect, onRunDetection }: QueueProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 space-y-3 border-b border-border-subtle">
        <h2 className="text-xs uppercase tracking-wide text-text-subtle">Suggestions</h2>
        <div className="flex flex-wrap gap-1.5">
          {(['pending', 'approved', 'rejected', 'cooled'] as StatusFilter[]).map(s => (
            <button key={s} type="button"
              onClick={() => onFilterChange(s)}
              aria-pressed={filter === s}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${
                filter === s
                  ? 'bg-accent-primary text-bg-surface border-accent-primary'
                  : 'border-border-subtle text-text-muted hover:border-border-strong'
              }`}>
              {s}
            </button>
          ))}
        </div>
        <button type="button" onClick={onRunDetection}
          className="w-full px-2 py-1.5 bg-bg-overlay border border-border-subtle rounded text-xs text-text-base hover:bg-border-subtle">
          Run detection (cluster 検出)
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-sm text-text-subtle">Loading…</div>
        ) : list.length === 0 ? (
          <EmptyState title="No suggestions" description={`Status: ${filter}`} />
        ) : (
          <ul role="list">
            {list.map(s => (
              <li key={s.id}>
                <button type="button"
                  onClick={() => onSelect(s.id)}
                  className={`w-full text-left px-4 py-3 border-b border-border-subtle ${selectedId === s.id ? 'bg-bg-overlay' : 'hover:bg-bg-overlay'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] uppercase tracking-wide ${s.status === 'pending' ? 'text-state-warning' : s.status === 'approved' ? 'text-state-success' : 'text-text-subtle'}`}>{s.status}</span>
                    <span className="text-xs text-text-subtle font-mono">{relativeTime(s.created_at)}</span>
                  </div>
                  <h3 className="text-sm text-text-base font-medium truncate">{s.proposed_title ?? s.cluster_key}</h3>
                  {s.proposed_one_line && <p className="text-xs text-text-muted line-clamp-1">{s.proposed_one_line}</p>}
                  <div className="text-[11px] text-text-subtle mt-1">{s.entry_ids.length} entries · {s.cluster_key}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

// ===== Hook: queue 取得 (filter/reload 連動) =====

function useSuggestionList(filter: StatusFilter) {
  const [list, setList] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const handler = () => setReloadKey(k => k + 1)
    window.addEventListener('collab_reload', handler)
    return () => window.removeEventListener('collab_reload', handler)
  }, [])

  useEffect(() => {
    const ac = new AbortController()
    setLoading(true)
    apiFetch<SuggestionsResp>('/api/article-suggestions', {
      query: { status: filter, limit: 50 },
      signal: ac.signal,
    })
      .then(res => setList(res.suggestions ?? []))
      .catch(() => setList([]))
      .finally(() => setLoading(false))
    return () => ac.abort()
  }, [filter, reloadKey])

  return { list, loading }
}

async function runDetection(show: (msg: string, opts?: { kind?: 'success' | 'error' | 'info' }) => void) {
  try {
    await apiFetch('/api/article-suggestions/run', { method: 'POST' })
    show('Detection started', { kind: 'success' })
    dispatchReload()
  } catch (e: unknown) {
    show(`Run failed: ${e instanceof Error ? e.message : 'unknown'}`, { kind: 'error' })
  }
}

// ===== CollaborateSidebar (デスクトップ Sub-sidebar) =====

export function CollaborateSidebar() {
  const [filter, setFilter] = useState<StatusFilter>(readFilter)
  const [selectedId, setSelectedId] = useState<string | null>(readSelected)
  const { list, loading } = useSuggestionList(filter)
  const { show } = useToast()

  useEffect(() => {
    const onSel = () => setSelectedId(readSelected())
    const onFilter = () => setFilter(readFilter())
    window.addEventListener('collab_selected_change', onSel)
    window.addEventListener('collab_filter_change', onFilter)
    return () => {
      window.removeEventListener('collab_selected_change', onSel)
      window.removeEventListener('collab_filter_change', onFilter)
    }
  }, [])

  return (
    <SuggestionQueue
      filter={filter}
      selectedId={selectedId}
      list={list}
      loading={loading}
      onFilterChange={(s) => { setFilter(s); writeFilter(s) }}
      onSelect={(id) => { setSelectedId(id); writeSelected(id) }}
      onRunDetection={() => runDetection(show)}
    />
  )
}

// ===== CollaborateLens (Main) =====

export function CollaborateLens() {
  const viewport = useViewport()
  const [filter, setFilter] = useState<StatusFilter>(readFilter)
  const [selectedId, setSelectedId] = useState<string | null>(readSelected)
  const { list, loading } = useSuggestionList(filter)
  const { show } = useToast()
  const [filterOpen, setFilterOpen] = useState(false)

  useEffect(() => {
    const onSel = () => setSelectedId(readSelected())
    const onFilter = () => setFilter(readFilter())
    window.addEventListener('collab_selected_change', onSel)
    window.addEventListener('collab_filter_change', onFilter)
    return () => {
      window.removeEventListener('collab_selected_change', onSel)
      window.removeEventListener('collab_filter_change', onFilter)
    }
  }, [])

  const selected = list.find(s => s.id === selectedId) ?? null

  // ===== Mobile: FilterBar に queue を入れる =====
  if (viewport === 'mobile') {
    return (
      <div className="h-full flex flex-col">
        <FilterBar label="Suggestions queue" open={filterOpen} onToggle={() => setFilterOpen(o => !o)}>
          <SuggestionQueue
            filter={filter}
            selectedId={selectedId}
            list={list}
            loading={loading}
            onFilterChange={(s) => { setFilter(s); writeFilter(s) }}
            onSelect={(id) => { setSelectedId(id); writeSelected(id); setFilterOpen(false) }}
            onRunDetection={() => runDetection(show)}
          />
        </FilterBar>
        <div className="flex-1 overflow-y-auto bg-bg-surface">
          {selected ? (
            <SuggestionDetail key={selected.id} suggestion={selected} onAction={() => { writeSelected(null); dispatchReload() }} />
          ) : (
            <div className="h-full flex items-center justify-center px-8 text-sm text-text-subtle text-center">
              Suggestion を選んでください<br/>
              (上の「Suggestions queue」を開く)
            </div>
          )}
        </div>
      </div>
    )
  }

  // ===== Desktop: Sub-sidebar で queue を表示するので Main は selected のみ =====
  return (
    <div className="h-full overflow-y-auto bg-bg-surface">
      {selected ? (
        <SuggestionDetail key={selected.id} suggestion={selected} onAction={() => { writeSelected(null); dispatchReload() }} />
      ) : (
        <div className="h-full flex items-center justify-center px-8 text-sm text-text-subtle">
          左の Sub-sidebar から suggestion を選択してください。
        </div>
      )}
    </div>
  )
}

// ===== SuggestionDetail (Main 内、numbered step actions) =====

function SuggestionDetail({ suggestion, onAction }: { suggestion: Suggestion; onAction: () => void }) {
  const { show } = useToast()
  const [busy, setBusy] = useState(false)
  const entryIds = suggestion.entry_ids ?? []

  async function reject_with_reason(reason: string) {
    setBusy(true)
    try {
      await apiFetch(`/api/article-suggestions/${suggestion.id}/reject`, { method: 'POST', body: { reason } })
      show('Rejected (cooldown 7 days)', { kind: 'success' })
      onAction()
    } catch (e: unknown) {
      show(`Reject failed: ${e instanceof Error ? e.message : 'unknown'}`, { kind: 'error' })
    } finally {
      setBusy(false)
    }
  }

  async function approve_with_id(articleId: string) {
    setBusy(true)
    try {
      await apiFetch(`/api/article-suggestions/${suggestion.id}/approve`, { method: 'POST', body: { article_id: articleId } })
      show('Approved', { kind: 'success' })
      onAction()
    } catch (e: unknown) {
      show(`Approve failed: ${e instanceof Error ? e.message : 'unknown'}`, { kind: 'error' })
    } finally {
      setBusy(false)
    }
  }

  function copyPrompt() {
    const prompt = [
      '/kdb-article-synthesize create',
      `proposed_title: ${suggestion.proposed_title ?? ''}`,
      `proposed_theme: ${suggestion.proposed_theme ?? ''}`,
      `proposed_applicability: ${suggestion.proposed_applicability ?? ''}`,
      `source_entry_ids:`,
      ...entryIds.map((id) => `  - ${id}`),
    ].join('\n')
    navigator.clipboard.writeText(prompt).then(
      () => show('Synthesis prompt copied to clipboard', { kind: 'success' }),
      () => show('Clipboard write failed', { kind: 'error' })
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-8 py-8 space-y-6">
      <header>
        <span className="text-[10px] uppercase tracking-wide text-text-subtle">Suggestion · {suggestion.status}</span>
        <h2 className="text-md font-medium text-text-base mt-1">{suggestion.proposed_title ?? suggestion.cluster_key}</h2>
        <p className="text-xs text-text-subtle mt-1">cluster: <span className="font-mono">{suggestion.cluster_key}</span> · {entryIds.length} entries</p>
      </header>

      <section>
        <h3 className="text-xs uppercase tracking-wide text-text-subtle mb-2">Proposed draft</h3>
        <dl className="text-sm space-y-2">
          {suggestion.proposed_one_line && <Row label="One-line">{suggestion.proposed_one_line}</Row>}
          {suggestion.proposed_theme && <Row label="Theme">{suggestion.proposed_theme}</Row>}
          {suggestion.proposed_applicability && <Row label="Applicability">{suggestion.proposed_applicability}</Row>}
          {suggestion.proposed_confidence && <Row label="Confidence">{suggestion.proposed_confidence}</Row>}
          {suggestion.draft_method && <Row label="Method">{suggestion.draft_method}</Row>}
        </dl>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wide text-text-subtle mb-2">Source entries ({entryIds.length})</h3>
        <ul className="text-xs text-text-muted font-mono space-y-1 max-h-[200px] overflow-y-auto">
          {entryIds.map((id) => (
            <li key={id}><EntryPreview id={id} /></li>
          ))}
        </ul>
      </section>

      {suggestion.status === 'pending' && (
        <section className="border-t border-border-subtle pt-6 space-y-4">
          <h3 className="text-xs uppercase tracking-wide text-text-subtle">Actions</h3>
          <ol className="text-sm space-y-3 text-text-base">
            <li className="flex gap-3 items-start">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-accent-primary text-bg-surface text-[10px] flex items-center justify-center font-bold">1</span>
              <div className="flex-1">
                <button type="button" onClick={copyPrompt} disabled={busy}
                  className="px-3 py-1.5 bg-accent-primary text-bg-surface rounded text-sm hover:opacity-90 disabled:opacity-60">
                  📋 Copy synthesis prompt
                </button>
                <p className="text-xs text-text-muted mt-1">→ Claude Code で <code className="bg-bg-overlay px-1 rounded">/kdb-article-synthesize</code> を実行して Article を作成</p>
              </div>
            </li>
            <li className="flex gap-3 items-start">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-border-strong text-text-subtle text-[10px] flex items-center justify-center font-bold">2</span>
              <div className="flex-1 space-y-2">
                <InlineApproveForm onApprove={(articleId) => approve_with_id(articleId)} busy={busy} />
              </div>
            </li>
            <li className="flex gap-3 items-start">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-border-strong text-text-subtle text-[10px] flex items-center justify-center font-bold">or</span>
              <div className="flex-1">
                <InlineRejectForm onReject={(reason) => reject_with_reason(reason)} busy={busy} />
              </div>
            </li>
          </ol>
        </section>
      )}
    </div>
  )
}

function EntryPreview({ id }: { id: string }) {
  const { item } = useDetailFetch(id, 'entry')
  return (
    <span>
      <span className="text-text-subtle">{id.slice(0, 8)}</span>
      {item && <span className="ml-2 text-text-base font-sans">{item.title}</span>}
    </span>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-28 flex-shrink-0 text-text-subtle">{label}</dt>
      <dd className="flex-1 text-text-base">{children}</dd>
    </div>
  )
}

function relativeTime(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime()
    const sec = Math.floor(ms / 1000)
    if (sec < 3600) return `${Math.floor(sec / 60)}m`
    if (sec < 86400) return `${Math.floor(sec / 3600)}h`
    return `${Math.floor(sec / 86400)}d`
  } catch { return '' }
}

// ===== Approve form with article_id autocomplete (recent articles datalist) =====

interface RecentArticleCache {
  fetchedAt: number
  items: { id: string; title: string }[]
}

function useRecentArticles() {
  const [items, setItems] = useState<{ id: string; title: string }[]>([])

  const fetch = useCallback(async () => {
    // 5 分以内のキャッシュは再利用
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(RECENT_ARTICLES_KEY) ?? 'null') as RecentArticleCache | null
      if (cached && Date.now() - cached.fetchedAt < 5 * 60 * 1000) {
        setItems(cached.items)
        return
      }
    } catch { /* ignore */ }

    try {
      const res = await apiFetch<SearchResponse>('/api/articles/search', { query: { sort: '-created_at', limit: 30 } })
      const next = (res.entries ?? []).map(h => ({ id: h.id, title: h.title }))
      setItems(next)
      try {
        window.sessionStorage.setItem(RECENT_ARTICLES_KEY, JSON.stringify({ fetchedAt: Date.now(), items: next }))
      } catch { /* ignore */ }
    } catch {
      setItems([])
    }
  }, [])

  return { items, fetch }
}

function InlineApproveForm({ onApprove, busy }: { onApprove: (articleId: string) => void; busy: boolean }) {
  const [id, setId] = useState('')
  const { items, fetch } = useRecentArticles()

  // form を開いた瞬間に recent articles を取得
  useEffect(() => { fetch() }, [fetch])

  return (
    <div className="flex gap-2 items-center flex-wrap">
      <input
        type="text"
        value={id}
        onChange={e => setId(e.target.value)}
        onFocus={() => fetch()}
        placeholder="Article ID (UUID or paste、recent から選択可)"
        list="collab-recent-articles"
        className="flex-1 min-w-[220px] px-2 py-1.5 bg-bg-base border border-border-subtle rounded text-sm font-mono placeholder:text-text-subtle focus:outline-none focus:border-border-strong"
      />
      <datalist id="collab-recent-articles">
        {items.map(a => (
          <option key={a.id} value={a.id}>{a.title}</option>
        ))}
      </datalist>
      <button type="button"
        disabled={busy || !id.trim() || id.trim().length < 30}
        onClick={() => onApprove(id.trim())}
        className="px-3 py-1.5 bg-bg-overlay border border-state-success text-state-success rounded text-sm hover:bg-state-success/10 disabled:opacity-60 whitespace-nowrap">
        Mark as approved
      </button>
    </div>
  )
}

function InlineRejectForm({ onReject, busy }: { onReject: (reason: string) => void; busy: boolean }) {
  const [reason, setReason] = useState('')
  const valid = reason.trim().length >= 5
  return (
    <div className="flex gap-2 items-center flex-wrap">
      <input
        type="text"
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder="Reject reason (5 文字以上)"
        className="flex-1 min-w-[220px] px-2 py-1.5 bg-bg-base border border-border-subtle rounded text-sm placeholder:text-text-subtle focus:outline-none focus:border-border-strong"
      />
      <button type="button"
        disabled={busy || !valid}
        onClick={() => onReject(reason.trim())}
        className="px-3 py-1.5 bg-bg-overlay border border-state-error text-state-error rounded text-sm hover:bg-state-error/10 disabled:opacity-60 whitespace-nowrap">
        Reject (cooldown 7d)
      </button>
    </div>
  )
}
